
import { useEffect, useRef } from "react";
import type { SavedSignature } from "@/types/signatures";

interface SignatureCanvasProps {
  signature: SavedSignature;
  canvasRef?: (el: HTMLCanvasElement | null) => void;
}

export const renderSvgToCanvas = (svgContent: string, canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Set canvas DPI for better quality
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  
  // Set display size
  canvas.style.width = rect.width + 'px';
  canvas.style.height = rect.height + 'px';
  
  // Set actual size in memory (scaled to account for extra pixel density)
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  
  // Scale context to device to ensure correct display size despite higher memory size
  ctx.scale(dpr, dpr);

  const img = new Image();
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);

  img.onload = () => {
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    
    // Reduce the scale by multiplying by 0.7 to make signature ~30% smaller
    const scale = Math.min(
      (canvas.width / dpr) / img.width,
      (canvas.height / dpr) / img.height
    ) * 0.7;
    
    const x = ((canvas.width / dpr) - img.width * scale) / 2;
    const y = ((canvas.height / dpr) - img.height * scale) / 2;
    
    ctx.save();
    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    ctx.restore();
    
    URL.revokeObjectURL(url);
  };
  
  img.src = url;
};

export const SignatureCanvas = ({ signature, canvasRef }: SignatureCanvasProps) => {
  const localCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = localCanvasRef.current;
    if (canvas && signature.svg_content) {
      renderSvgToCanvas(signature.svg_content, canvas);
    }
  }, [signature.svg_content, signature.id]);

  return (
    <canvas
      ref={(el) => {
        localCanvasRef.current = el;
        if (canvasRef) canvasRef(el);
      }}
      width={800}
      height={400}
      className="w-full h-full object-contain"
    />
  );
};
